import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import SpauthServiceContext from '../contexts/SpauthContext';
import ApiKeyLoader from './ApiKeyLoader';
import { convertDynamoDBToJSON } from '../utils/dynamoDbConverter';
import { formatTimestamp } from '../utils/formatTimestamp';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Button,
    CircularProgress,
    TextField,
    Autocomplete,
    Chip
} from '@mui/material';

const ApiTable = () => {
    const NOT_AVAILABLE = 'N/A';
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [spauthToken, setSpauthToken] = useState();
    const [searchTerm, setSearchTerm] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [colWidths, setColWidths] = useState({
        team: 150, rank: 80, accountIds: 200, accountCount: 90,
        hashedKey: 200, createdBy: 130, email: 180, spauthUsername: 150,
        createdAt: 140, actions: 120
    });
    const dragState = useRef(null);
    const autocompleteContainerRef = useRef(null);


    const escapeCsvValue = (value) => {
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
    };

    const handleDownloadCsv = () => {
        if (filteredApiKeys.length === 0) {
            return;
        }

        const headers = ['Team', 'Rank', 'Account IDs', 'Hashed Key', 'Created By', 'Created Date'];

        const rows = filteredApiKeys.map((row) => [
            row.team || NOT_AVAILABLE,
            row.rank || NOT_AVAILABLE,
            Array.isArray(row.accountIds) ? row.accountIds.join(', ') : NOT_AVAILABLE,
            row.hashedKey || NOT_AVAILABLE,
            row.createdBy || NOT_AVAILABLE,
            formatTimestamp(row.createdAt) || NOT_AVAILABLE
        ]);

        const csvContent = [headers, ...rows]
            .map((csvRow) => csvRow.map(escapeCsvValue).join(','))
            .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `api-keys-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    const onResizeStart = useCallback((col, e) => {
        e.preventDefault();
        dragState.current = { col, startX: e.clientX, startWidth: colWidths[col] };

        const onMouseMove = (e) => {
            if (!dragState.current) return;
            const delta = e.clientX - dragState.current.startX;
            const newWidth = Math.max(50, dragState.current.startWidth + delta);
            setColWidths(prev => ({ ...prev, [dragState.current.col]: newWidth }));
        };

        const onMouseUp = () => {
            dragState.current = null;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [colWidths]);

    const StickyContent = ({ children, sx: extraSx }) => (
        <Box sx={{
            position: 'sticky',
            top: 57,
            zIndex: 1,
            backgroundColor: 'white',
            py: 1,
            ...extraSx,
        }}>
            {children}
        </Box>
    );

    const ResizableHeader = ({ col, children }) => (
        <TableCell
            sx={{
                color: 'white', fontWeight: 'bold', position: 'sticky',
                top: 0, zIndex: 3, backgroundColor: '#1976d2',
                width: colWidths[col], minWidth: colWidths[col], maxWidth: colWidths[col],
                overflow: 'hidden', whiteSpace: 'nowrap'
            }}
        >
            {children}
            <Box
                onMouseDown={(e) => onResizeStart(col, e)}
                sx={{
                    position: 'absolute', right: 0, top: 0, bottom: 0, width: 4,
                    cursor: 'col-resize', userSelect: 'none',
                    backgroundColor: '#508fff',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                }}
            />

        </TableCell>
    );

    const contextValue = useContext(SpauthServiceContext);
    // Update spauthToken from context when available
    useEffect(() => {
        if (contextValue && contextValue.token) {
            setSpauthToken(contextValue.token);
        }
    }, [contextValue]);

    // Filter API keys based on search term
    const filteredApiKeys = apiKeys.filter((key) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (key.team && key.team.toLowerCase().includes(searchLower)) ||
            (key.rank && key.rank.toString().toLowerCase().includes(searchLower)) ||
            (key.hashedKey && key.hashedKey.toLowerCase().includes(searchLower)) ||
            (key.createdDate && key.createdDate.toLowerCase().includes(searchLower)) ||
            (Array.isArray(key.accountIds) && key.accountIds.some(id => id.toLowerCase().includes(searchLower)))
        );
    });


    const handleGenerateTable = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('/dynamo/scan', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'foo': spauthToken

                },

            });

            if (!response.ok) {
                throw new Error('Failed to fetch API keys');
            }

            const data = await response.json();
            const items = data.Items || data || [];

            // Convert DynamoDB format to clean JSON
            const cleanData = convertDynamoDBToJSON(items);
            setApiKeys(cleanData);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [spauthToken]);
    useEffect(() => {
        handleGenerateTable();
    }, [handleGenerateTable]);

    const handleEditClick = (index, row) => {
        setEditingIndex(index);
        const ids = Array.isArray(row.accountIds) ? [...row.accountIds] : [];
        setEditData({
            rank: row.rank || '',
            accountIds: ids,
            accountIdsRaw: ids.join(', '),
            hashedKey: row.hashedKey
        });
        setSaveError(null);
        setTimeout(() => {
            const el = autocompleteContainerRef.current?.querySelector('.MuiInputBase-root');
            if (el) el.scrollTop = el.scrollHeight;
        }, 50);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditData({});
        setSaveError(null);
    };

    const handleSaveEdit = async (index) => {
        setSaving(true);
        setSaveError(null);

        const accountIdsArray = showAdvanced
            ? (editData.accountIdsRaw || '').split(/[\n,]+/).map(id => id.trim()).filter(id => id.length > 0)
            : editData.accountIds.filter(id => id.trim().length > 0);

        const requestBody = {
            hashedKey: editData.hashedKey,
            updates: {
                accountIds: accountIdsArray,
                rank : editData.rank
            }
        };

        try {
            const response = await fetch('/dynamo/', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'foo': spauthToken
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Failed to save changes (${response.status})`);
            }

            setApiKeys(prev => prev.map((row, i) =>
                i === index
                    ? { ...row, rank: editData.rank, accountIds: accountIdsArray }
                    : row
            ));
            setEditingIndex(null);
            setEditData({});
            await handleGenerateTable();
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    };


    return (
        <Box sx={{ width: 'auto', margin: 0, padding: '2rem' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3, gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleGenerateTable}
                        disabled={loading}
                        sx={{
                            backgroundColor: 'green',
                            '&:hover': { backgroundColor: 'darkgreen' }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Reload Table'}
                    </Button>
                    <Button
                        variant={showAdvanced ? 'contained' : 'outlined'}
                        onClick={() => setShowAdvanced(prev => !prev)}
                    >
                        Advanced
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDownloadCsv}
                        disabled={loading || filteredApiKeys.length === 0}
                    >
                        Download CSV
                    </Button>
                </Box>

                {apiKeys.length > 0 && (
                    <Box sx={{
                        flex: 1,
                        maxWidth: 400,
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <input
                            type="text"
                            placeholder="Search by team, rank, account IDs, or hashed key..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 15px',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                            onBlur={(e) => e.target.style.borderColor = '#ccc'}
                        />
                    </Box>
                )}
            </Box>
            {error && (
                <Typography variant="body1" color="error" sx={{ marginBottom: 2 }}>
                    Error: {error}
                </Typography>
            )}
            {saveError && (
                <Typography variant="body1" color="error" sx={{ marginBottom: 2 }}>
                    Save error: {saveError}
                </Typography>
            )}

            {loading ? (
                <ApiKeyLoader
                    heading="Generating API Table..."
                    description="Please wait while we fetch your API table data.."
                />
            ) : (
                <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    <Table sx={{ tableLayout: 'fixed' }} aria-label="api keys table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                <ResizableHeader col="team">Team</ResizableHeader>
                                <ResizableHeader col="rank">Rank</ResizableHeader>
                                <ResizableHeader col="accountIds">Account IDs</ResizableHeader>
                                <ResizableHeader col="accountCount"># Accounts</ResizableHeader>
                                <ResizableHeader col="hashedKey">Hashed Key</ResizableHeader>
                                {showAdvanced && <>
                                    <ResizableHeader col="createdBy">Created By</ResizableHeader>
                                    <ResizableHeader col="email">Email</ResizableHeader>
                                    <ResizableHeader col="spauthUsername">Spauth Username</ResizableHeader>
                                    <ResizableHeader col="createdAt">Created Date</ResizableHeader>
                                </>}
                                <ResizableHeader col="actions">Actions</ResizableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredApiKeys.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={showAdvanced ? 10 : 6} align="center">
                                        <Typography variant="body1" sx={{ padding: 3, color: 'text.secondary' }}>
                                            {searchTerm ? `No results found for "${searchTerm}"` : 'No API keys available'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredApiKeys.map((row, index) => {
                                    const isEditing = editingIndex === index;
                                    return (
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, '& td': { verticalAlign: 'top' } }}
                                        >
                                            <TableCell><StickyContent>{row.team || 'N/A'}</StickyContent></TableCell>
                                            <TableCell>
                                                <StickyContent>
                                                    {isEditing ? (
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={editData.rank}
                                                            onChange={(e) => setEditData(prev => ({ ...prev, rank: e.target.value }))}
                                                            slotProps={{ htmlInput: { min: 0, max: 10, step: 1 } }}
                                                            sx={{ width: 80 }}
                                                        />
                                                    ) : (row.rank || 'N/A')}
                                                </StickyContent>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 220 }}>
                                                {isEditing ? (
                                                    showAdvanced ? (
                                                        <TextField
                                                            size="small"
                                                            multiline
                                                            rows={4}
                                                            value={editData.accountIdsRaw}
                                                            onChange={(e) => setEditData(prev => ({ ...prev, accountIdsRaw: e.target.value }))}
                                                            placeholder="Paste or type comma-separated IDs"
                                                            helperText={`${(editData.accountIdsRaw || '').split(/[\n,]+/).map(id => id.trim()).filter(id => id.length > 0).length} IDs`}
                                                            sx={{ width: '100%' }}
                                                        />
                                                    ) : (
                                                        <Box ref={autocompleteContainerRef} sx={{ width: '100%', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.23)', borderRadius: 1 }}>
                                                        <Autocomplete
                                                            multiple
                                                            freeSolo
                                                            disableClearable
                                                            options={[]}
                                                            value={editData.accountIds}
                                                            onChange={(_, newValue) =>
                                                                setEditData(prev => ({ ...prev, accountIds: newValue }))
                                                            }
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => (
                                                                    <Chip
                                                                        label={option}
                                                                        size="small"
                                                                        {...getTagProps({ index })}
                                                                        key={index}
                                                                    />
                                                                ))
                                                            }
                                                            sx={{ width: '100%' }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder="+ Add Account ID"
                                                                    helperText={`${editData.accountIds.length} IDs`}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            overflow: 'hidden',
                                                                            padding: '6px 6px 6px 6px',
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            border: 'none',
                                                                        },
                                                                        '& .MuiInputBase-root': {
                                                                            maxHeight: 120,
                                                                            overflowY: 'auto',
                                                                            overflowX: 'hidden',
                                                                            flexWrap: 'wrap',
                                                                            alignItems: 'flex-start',
                                                                            alignContent: 'flex-start',
                                                                        },
                                                                        '& .MuiAutocomplete-tag': {
                                                                            maxWidth: '100%',
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                        </Box>
                                                    )
                                                ) : (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        gap: 0.5,
                                                        maxHeight: 120,
                                                        overflowY: 'auto',
                                                        padding: '6px',
                                                    }}>
                                                        {Array.isArray(row.accountIds) && row.accountIds.length > 0
                                                            ? row.accountIds.map((id, i) => (
                                                                <Chip key={i} label={id} size="small" />
                                                            ))
                                                            : 'N/A'
                                                        }
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <StickyContent>{Array.isArray(row.accountIds) ? row.accountIds.length : 0}</StickyContent>
                                            </TableCell>
                                            <TableCell sx={{
                                                maxWidth: 200,
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem'
                                            }}>
                                                <StickyContent sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {row.hashedKey || 'N/A'}
                                                </StickyContent>
                                            </TableCell>
                                            {showAdvanced && <>
                                                <TableCell><StickyContent>{row.createdBy || 'N/A'}</StickyContent></TableCell>
                                                <TableCell><StickyContent>{row.createdByEmail || 'N/A'}</StickyContent></TableCell>
                                                <TableCell><StickyContent>{row.spauthUsername || 'N/A'}</StickyContent></TableCell>
                                                <TableCell><StickyContent>{formatTimestamp(row.createdAt)}</StickyContent></TableCell>
                                            </>}
                                            <TableCell>
                                                <StickyContent>
                                                    {isEditing ? (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => handleSaveEdit(index)}
                                                                disabled={saving}
                                                                sx={{ backgroundColor: 'green', '&:hover': { backgroundColor: 'darkgreen' } }}
                                                            >
                                                                {saving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleEditClick(index, row)}
                                                        >
                                                            Edit
                                                        </Button>
                                                    )}
                                                </StickyContent>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

ApiTable.propTypes = {
    spauthToken: PropTypes.string
};

export default ApiTable;
